import React, { forwardRef, useRef, useImperativeHandle } from "react";
import styled from "styled-components";
import { tokens } from "../../public/tokens";

const sizes = {
    sm: {
        outerSize: "16px",
        innerSize: "8px",
        fontSize: tokens.typography.fontSize["md-12px"],
        lineHeight: "16px",
    },
    md: {
        outerSize: "20px",
        innerSize: "10px",
        fontSize: tokens.typography.fontSize["lg-14px"],
        lineHeight: "20px",
    },
    lg: {
        outerSize: "24px",
        innerSize: "14px",
        fontSize: tokens.typography.fontSize["lg-14px"],
        lineHeight: "20px",
    },
};

const defaultFocusVisibleState = {
    outline: `2px solid ${tokens.colors.brand.blue["500"]}`,
    outlineOffset: "2px",
};

const errorState = {
    outline: `1px solid ${tokens.colors.ui.destructive}`,
    boxShadow: "0px 0px 1px 1px rgba(196, 61, 81, 0.20)",
};

const variantConfig = {
    primary: {
        background: tokens.colors.ui.primary,
        color: tokens.colors.ui["primary-foreground"],
        hover: { background: tokens.colors.brand["aie-blue"]["600"] },
        active: { background: tokens.colors.brand["aie-blue"]["700"] },
    },
    secondary: {
        background: tokens.colors.brand.blue["100"],
        color: tokens.colors.brand["aie-blue"]["500"],
        hover: { background: tokens.colors.brand.blue["200"] },
        active: { background: tokens.colors.brand.blue["300"] },
    },
    destructive: {
        background: tokens.colors.states.alert["50"],
        color: tokens.colors.ui.destructive,
        hover: { background: tokens.colors.states.alert["100"] },
        active: { background: tokens.colors.states.alert["50"] },
    },
};

const getVariantStyles = (variant) => {
    const config = variantConfig[variant] || variantConfig.primary;
    const { background, color, hover, active } = config;
    return { background, color, hover, active };
};

const StyledHiddenRadio = styled.input.attrs({ type: "radio" })(() => ({
    position: "absolute",
    width: "1px",
    height: "1px",
    margin: "-1px",
    padding: 0,
    overflow: "hidden",
    clip: "rect(0 0 0 0)",
    whiteSpace: "nowrap",
    border: 0,
}));

const StyledRadioOuter = styled.span(
    ({ $size = "lg", $variant = "primary", $checked = false }) => {
        const { outerSize, innerSize, fontSize, lineHeight } = sizes[$size];
        const variantStyles = getVariantStyles($variant);

        return {
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            width: outerSize,
            height: outerSize,
            borderRadius: "9999px",
            border: `1.5px solid ${tokens.colors.ui.border}`,
            transition: "all 0.15s ease-in-out",
            background: $checked ? variantStyles.background : tokens.colors.ui.background,
            borderColor: $checked ? variantStyles.background : tokens.colors.ui.border,
            position: "relative",
            boxShadow: "0px 0px 6px rgba(55, 59, 64, 0.05)",

            "&:hover": {
                ...variantStyles.hover,
                borderColor: $checked ? variantStyles.background : tokens.colors.brand.blue["500"],
            },
            "&:active": variantStyles.active,

            "&::after": {
                content: $checked ? '""' : "none",
                display: "block",
                width: innerSize,
                height: innerSize,
                borderRadius: "9999px",
                background: variantStyles.color,
                transition: "all 0.15s ease-in-out",
            },
        };
    }
);

const StyledRadioWrapper = styled.label(({ $size = "lg" }) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "10px",
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: sizes[$size].fontSize,
    fontWeight: tokens.typography.fontWeights.normal,
    color: tokens.colors.ui.foreground,
    cursor: "pointer",

    "&:has(input:disabled)": {
        opacity: 0.5,
        cursor: "not-allowed",
    },

    "input:focus-visible + span": defaultFocusVisibleState,

    'input[data-error="true"] + span': errorState,
}));

/**
 * A radio component with support for multiple variants, sizes, and states.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.size="lg"] - Size variant: "sm", "md", or "lg"
 * @param {string} [props.variant="primary"] - Visual variant: "primary", "secondary", or "destructive"
 * @param {string} [props.name] - Name attribute for the radio group
 * @param {string} [props.value] - Value of the radio button
 * @param {boolean} [props.checked=false] - Whether the radio is selected
 * @param {boolean} [props.disabled=false] - If true, the radio is disabled
 * @param {boolean} [props.error=false] - If true, applies error styling
 * @param {string} [props.label] - Label text to display next to the radio
 * @param {function} [props.onChange] - Callback fired when the radio state changes
 * @param {React.Ref} ref - Forwarded ref to the underlying input element
 * @returns {JSX.Element} The rendered radio component
 */
const Radio = forwardRef(
    (
        {
            size = "lg",
            variant = "primary",
            name,
            value,
            checked = false,
            disabled = false,
            error = false,
            label,
            onChange,
            ...rest
        },
        ref
    ) => {
        const internalRef = useRef(null);
        useImperativeHandle(ref, () => internalRef.current);

        return (
            <StyledRadioWrapper $size={size}>
                <StyledHiddenRadio
                    ref={internalRef}
                    name={name}
                    value={value}
                    checked={checked}
                    disabled={disabled}
                    onChange={onChange}
                    data-error={error ? "true" : undefined}
                    {...rest}
                />
                <StyledRadioOuter $size={size} $variant={variant} $checked={checked} />
                {label && <span>{label}</span>}
            </StyledRadioWrapper>
        );
    }
);

Radio.displayName = "Radio";

export default Radio;